import { ErrorMessageWrapper } from "@/common/components/ErrorMessageWrapper";
import Table from "@/common/components/Table";
import { useFilterDate } from "@/common/hooks/useFilterDate";
import { useFilterParams } from "@/common/hooks/useFilterParams";
import { Mapper } from "@/common/services/mapper";
import { usePaymentAdvances } from "@/features/paymentAdvances/hooks/usePaymentAdvances";
import { PaymentAdvancesTransformer } from "@/features/paymentAdvances/services/paymentAdvancesTransformer";
import { PaymentAdvanceData, PaymentAdvanceTableItem } from "@/features/paymentAdvances/types/paymentAdvance.type";
import { ChevronsRightIcon } from "lucide-react";
import { useParams } from "react-router-dom";
import ListFilters from "@/common/components/ListFilters";
import { DatePickerWithRange } from "@/common/components/ui/dateRangePicker";
import { paymentAdvanceStatusOptions } from "@/features/paymentAdvances/constants/paymentAdvanceStatus.constant";
import GoBackButton from "@/common/components/GoBackButton";
import useEstablishmentCompany from "@/features/establishments/hooks/useEstablishmentCompany";
import { establishmentPaymentAdvancesColumns } from "@/features/establishments/constants/establishmentPaymentAdvancesColumns";

const EstablishmentBillPaymentAdvancesList = () => {
  const { establishmentId } = useParams();

  const { data: establishmentCompany } = useEstablishmentCompany(establishmentId);

  const { filterParams, setFilterParams } = useFilterParams();
  const { date, formattedDate } = useFilterDate(filterParams);

  const {
    data: paymentAdvances,
    meta,
    isLoading: isLoadingPaymentAdvances,
    error: paymentAdvancesError = {} as any,
    refetch: refetchPaymentAdvances,
  } = usePaymentAdvances<PaymentAdvanceTableItem[]>({
    filters: {
      page: filterParams.page,
      status: filterParams.status?.value,
      createdAtStartDate: formattedDate.from,
      createdAtEndDate: formattedDate.to,
      establishmentCompanyId: establishmentId,
    },
    mapper: Mapper.mapWithTransformer<PaymentAdvanceData[], PaymentAdvanceTableItem[]>({
      transformer: PaymentAdvancesTransformer.tableTransformer(),
    }),
  });

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <div className="flex gap-x-4 items-center">
          <GoBackButton fallback={`/establishments/${establishmentId}/bills`} searchLastRouteFromFallback />

          <p className="text-white-400 font-normal">
            {establishmentCompany?.general_information?.fantasy_name}
          </p>

          <ChevronsRightIcon color="#ffffffb3" />

          <h2>
            Antecipáveis de fatura
          </h2>
        </div>
      </div>

      <ErrorMessageWrapper
        className="h-[calc(100vh-64px-32px-40px)]"
        statusCode={paymentAdvancesError?.status}
        refetchFunction={refetchPaymentAdvances}
      >
        <div className="flex flex-col gap-6">
          <div className="flex flex-col gap-6">
            <div className="flex items-center gap-2">
              <DatePickerWithRange date={date} setDate={(date) => setFilterParams((params) => ({ ...params, date }))} />

              <ListFilters
                filters={[
                  {
                    className: 'w-40',
                    onChange: (e: any) => setFilterParams((prev) => ({ ...prev, status: e?.value ? e : null })),
                    value: filterParams.status || '',
                    options: [{ label: 'Todos', value: '' }, ...(paymentAdvanceStatusOptions || [])],
                    placeholder: 'Status',
                  },
                ]}
              />
            </div>
          </div>

          <div>
            <Table
              className="max-h-[calc(100vh-196px)]"
              columns={establishmentPaymentAdvancesColumns}
              isLoadingData={isLoadingPaymentAdvances}
              rows={paymentAdvances || []}
              totalPages={meta?.total_pages}
              totalCount={meta?.total}
              hasPagination
              rowEmptyValue="--"
              page={filterParams.page ? Number(filterParams.page) : 1}
              setPage={(page) => setFilterParams((prev) => ({ ...prev, page }))}
            />
          </div>
        </div>
      </ErrorMessageWrapper>
    </div>
  );
};

export default EstablishmentBillPaymentAdvancesList;
